import {
  AbstractControl,
  FormArray,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { TsRange } from '@techniek-team/tt-range';
import { areIntervalsOverlapping, parse } from 'date-fns';
import { SetRequired } from 'type-fest';

interface TimeRangeValidatorOptions {
  startTimeName: string;
  endTimeName: string;
  dateControlName?: string; // if not give we assume that there no separate date field.
  format?: string;
}

export function noOverlappingTimeSlotsValidator(
  options: Partial<TimeRangeValidatorOptions>,
): ValidatorFn {
  const inputConfig = {
    startTimeName: 'startTime',
    endTimeName: 'endTime',
    format: 'HH:mm',
    ...options,
  };
  return (control: AbstractControl): ValidationErrors | null => {
    if (!(control instanceof FormArray)) {
      return null;
    }

    const timeSlots = control.value;
    if (!timeSlots || timeSlots.length <= 1) {
      return null;
    }

    // Create ranges and check for overlaps
    const ranges = createRangesFromTimeSlots(control, inputConfig);
    return findOverlappingRanges(ranges);
  };
}

function createRangesFromTimeSlots(
  timeSlotsFormArray: FormArray,
  config: SetRequired<TimeRangeValidatorOptions, 'format'>,
): { index: number; range: TsRange }[] {
  return timeSlotsFormArray.controls
    .map((control, index) => createRangeFromTimeSlot(control, index, config))
    .filter((s) => s !== null);
}

function createRangeFromTimeSlot(
  timeSlotControl: AbstractControl,
  index: number,
  config: SetRequired<TimeRangeValidatorOptions, 'format'>,
): { index: number; range: TsRange } | null {
  if (!(timeSlotControl instanceof FormGroup)) {
    throw new Error('This method can only be used on a FormGroup.');
  }

  if (!timeSlotControl.value[config.startTimeName] || !timeSlotControl.value[config.endTimeName]) {
    return null;
  }

  const date = config.dateControlName ? timeSlotControl.value[config.dateControlName] : new Date();

  return {
    index: index,
    range: new TsRange(
      parse(timeSlotControl.value[config.startTimeName], config.format, date),
      parse(timeSlotControl.value[config.endTimeName], config.format, date),
    ),
  };
}

function findOverlappingRanges(
  ranges: { index: number; range: TsRange }[],
): ValidationErrors | null {
  for (let i = 0; i < ranges.length; i++) {
    for (let j = i + 1; j < ranges.length; j++) {
      if (areIntervalsOverlapping(ranges[i].range, ranges[j].range)) {
        return {
          overlappingTimeSlots: {
            firstIndex: ranges[i].index,
            secondIndex: ranges[j].index,
          },
        };
      }
    }
  }
  return null;
}
